import React, { useCallback, useRef } from 'react'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { isShapeTextSupported } from '@/lib/shapes/shape-text-frame'
import {
  useActiveObjectActions,
  useActiveSelection,
} from '@/components/app/editor/active-object-context'
import { useCandidAutoTagging } from '@/api/hooks/use-candid-auto-tagging'
import { useCandidDetail } from '@/api/hooks/use-candid-detail'
import { hasNamedCaptionTags } from '@/lib/caption/build-caption-paragraphs'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import { isSingleFrozenSelectionForUnfreeze } from '@/components/app/editor/select-all-policy'
import { selectionIncludesSelfFrozenForDelete } from '@/lib/editor/freeze-object-messages'
import { finalizeActiveShapeTextEdit } from '@/lib/shapes/shape-text-edit-session'
import type { HomeFoundation } from '../../types/home-groups'

/**
 * Tool state (active tool, shape, pen, point type, node edit), pan mode and its announcement /
 * Escape exit, the menu selection flags read from the live selection, and the selected photo with
 * its candid queries.
 */
export function useHomeToolsAndSelection(
  deps: Pick<HomeFoundation, 'shapeManagerRef' | 'centerCanvasRef' | 'leftPanelRef'>
) {
  const { shapeManagerRef, centerCanvasRef, leftPanelRef } = deps

  // Shape state
  const [selectedShape, setSelectedShape] = React.useState<ShapeType | null>(null)
  const [selectedPenTool, setSelectedPenTool] = React.useState<string | null>(null)
  const [selectedPointType, setSelectedPointType] = React.useState<string | null>('curve')
  const [isNodeEditMode, setIsNodeEditMode] = React.useState(false)
  const [activeObjectId, setActiveObjectId] = React.useState<string | null>(null)
  // Without live position, so a drag that moves the selection doesn't re-render home every frame.
  const { activeObjects } = useActiveSelection()
  const { setActiveObject, setActiveObjects, setActiveTextBoxes } = useActiveObjectActions()
  const [activeItem, setActiveItem] = React.useState<string>('select')
  const [isPanModeActive, setIsPanModeActive] = React.useState(false)
  const [panAnnouncement, setPanAnnouncement] = React.useState('')
  const [hasSelection, setHasSelection] = React.useState(false)
  const [hasTextSelection, setHasTextSelection] = React.useState(false)
  const canZoomToSelection = hasSelection || hasTextSelection
  const lastPanModeRef = useRef(isPanModeActive)
  // Read the live selection straight from the shape/text managers (same source the object
  // context menu uses) rather than the `activeObjects`/`selectedTextBoxPanelIds` React-state
  // mirrors, which can go stale relative to the real selection (see WMPCM-6294).
  const { shapeSnapshots: activeObjectsForMenus, textSnapshots: selectedTextBoxesForMenus } =
    centerCanvasRef.current?.getSelectionSnapshotsForMenus() ?? {
      shapeSnapshots: activeObjects,
      textSnapshots: [],
    }
  const hasObjectSelectionForMenus =
    activeObjectsForMenus.length + selectedTextBoxesForMenus.length > 0
  const hasLockedSelectionForMenus =
    activeObjectsForMenus.some((shape) => shape.locked === true) ||
    selectedTextBoxesForMenus.some((box) => box.locked === true)
  const hasSelfFrozenSelectionForMenus = selectionIncludesSelfFrozenForDelete(
    activeObjectsForMenus,
    selectedTextBoxesForMenus
  )
  const showUnlockSelectedInObjectMenus =
    hasObjectSelectionForMenus &&
    activeObjectsForMenus.every((shape) => shape.locked === true) &&
    selectedTextBoxesForMenus.every((box) => box.locked === true)
  const showUnfreezeSelectedInObjectMenus = isSingleFrozenSelectionForUnfreeze(
    activeObjectsForMenus,
    selectedTextBoxesForMenus
  )
  const hasLockedSelectedTextForPanels = selectedTextBoxesForMenus.some(
    (box) => box.locked === true
  )
  const hasAnyLockedObjects = (shapeManagerRef.current?.getShapes() ?? []).some(
    (shape) => shape.locked === true
  )
  const hasAnyFrozenObjects = (shapeManagerRef.current?.getShapes() ?? []).some(
    (shape) => shape.frozen === true
  )

  // When user selects a shape type in Shapes tool (pentagon, etc.), clear canvas selection so Object panel stays disabled until they explicitly select a shape again
  const handleSetSelectedShape = React.useCallback(
    (shape: ShapeType | null) => {
      if (shape != null) {
        setActiveObject(null)
        setActiveObjectId(null)
        // Exit pen-drawing and node-edit modes so any in-progress path is committed
        // (PenToolOverlay unmount cleanup auto-commits if new points were drawn) and
        // the shape tool activates cleanly without residual pen-edit state.
        setSelectedPenTool(null)
        setIsNodeEditMode(false)
      }

      setSelectedShape(shape)
    },
    [setActiveObject]
  )

  const deactivatePanMode = React.useCallback(() => {
    setIsPanModeActive(false)
  }, [])

  const togglePanMode = React.useCallback(() => {
    setIsPanModeActive((prev) => !prev)
  }, [])

  const handleActivateTool = React.useCallback(
    (item: string) => {
      finalizeActiveShapeTextEdit(shapeManagerRef.current)
      setIsPanModeActive(false)

      // Switching tools away from an active Edit Path session must end that session first —
      // otherwise CenterCanvas's internal node-edit state survives the switch, and its
      // "clicks inside the edited shape's bounds stay in node edit and do nothing" pointer-down
      // guard silently swallows the next click on that shape under whatever tool is now active.
      // Concretely: converting a single text box to a shape auto-enters node-edit mode on the
      // result, and clicking the Type-tool rail button (unlike picking a Shapes-panel preset,
      // which already exits node-edit mode via its own effect) never told CenterCanvas the
      // session was over — so clicking the freshly-converted shape with the Type tool did
      // nothing until a reload reset the mode entirely.
      if (isNodeEditMode) {
        centerCanvasRef.current?.exitNodeEditMode()
      }

      if (item !== 'shape') {
        setSelectedShape(null)
        setSelectedPenTool(null)
      }

      if (item === 'type') {
        const mgr = shapeManagerRef.current
        const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []
        const shapeId = selectedShapeIds.length === 1 ? selectedShapeIds[0]! : null
        const shape = shapeId ? mgr?.getShapes().find((s) => s.id === shapeId) : undefined
        const willEnterEdit = !!(shape && !shape.locked && isShapeTextSupported(shape))

        // `focusToolButton: false` when we're about to enter edit mode below — its own
        // `setTimeout(..., 0)` refocus of the tool-rail button would otherwise fire right after
        // the textarea grabs focus for editing, stealing it back and blurring the textarea, which
        // immediately exits edit mode again (the "flashes then reverts to the placeholder" bug).
        leftPanelRef.current?.expandAndOpenType?.(
          willEnterEdit ? { focusToolButton: false } : undefined
        )

        if (willEnterEdit) {
          mgr?.enterShapeTextEdit(shapeId!)
        }
      }
    },
    [isNodeEditMode, centerCanvasRef, leftPanelRef, shapeManagerRef]
  )

  /** Edit → Check Spelling and the text context menus open the left Spell Check panel. */
  const handleCheckSpelling = useCallback(() => {
    leftPanelRef.current?.openSpellCheckPanel?.()
  }, [leftPanelRef])

  React.useEffect(() => {
    if (lastPanModeRef.current === isPanModeActive) return
    lastPanModeRef.current = isPanModeActive
    setPanAnnouncement(isPanModeActive ? 'Pan mode activated' : 'Pan mode deactivated')
    const timeoutId = window.setTimeout(() => setPanAnnouncement(''), 750)

    return () => window.clearTimeout(timeoutId)
  }, [isPanModeActive])

  React.useEffect(() => {
    if (!isPanModeActive) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return

      const target = event.target as HTMLElement | null
      const isEditable =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable ||
          target.getAttribute('role') === 'textbox')

      if (isEditable) return

      event.preventDefault()
      event.stopPropagation()
      deactivatePanMode()
    }

    window.addEventListener('keydown', onKeyDown, true)

    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [deactivatePanMode, isPanModeActive])

  // Primitive dep to avoid firing on every canvas sync (activeObjects array reallocates constantly).
  const selectedPhotoId = React.useMemo(() => {
    if (activeObjects.length !== 1) return null

    const shape = activeObjects[0]

    return resolvePlacedPhotoIdentity(shape) ? shape.id : null
  }, [activeObjects])

  const selectedPhotoImageId = React.useMemo(() => {
    if (activeObjects.length !== 1) return null

    return resolvePlacedPhotoIdentity(activeObjects[0])?.imageId ?? null
  }, [activeObjects])

  const { data: candidAutoTaggingData } = useCandidAutoTagging(selectedPhotoImageId)
  const candidAutoTaggingDataRef = useRef(candidAutoTaggingData)

  candidAutoTaggingDataRef.current = candidAutoTaggingData

  const { data: candidDetail } = useCandidDetail(selectedPhotoImageId)
  const isSelectedPhotoPortrait = !!candidDetail?.lastMadePortraitDate
  const isCaptionEnabled =
    selectedPhotoImageId !== null &&
    (isSelectedPhotoPortrait || hasNamedCaptionTags(candidAutoTaggingData))

  // Which Properties Panel section auto-opens for a given selection (Object/Type/Photo, per
  // `computePropertiesPanelSelectionSignature`) is now centralized inside `SelectTool` via
  // `usePropertiesPanelAutoOpenSection` — it reacts to `activeObjects`/`activeTextBoxes` directly
  // (the same selection this file tracks) and already respects a user's explicit close per
  // selection, so no ad hoc "open Photos on photo select" effect is needed here. This also
  // resolves WMPCM-10142 (right-click a photo, click "Remove Photo" with the panel closed, panel
  // shouldn't reopen): the new hook only ever toggles which accordion section is open, never the
  // panel's own expanded/collapsed state, so it has no way to force the panel open.

  return {
    activeObjects,
    setActiveObject,
    setActiveObjects,
    setActiveTextBoxes,
    activeObjectsForMenus,
    selectedTextBoxesForMenus,
    candidAutoTaggingData,
    candidDetail,
    selectedShape,
    setSelectedShape,
    selectedPenTool,
    setSelectedPenTool,
    selectedPointType,
    setSelectedPointType,
    isNodeEditMode,
    setIsNodeEditMode,
    activeObjectId,
    setActiveObjectId,
    activeItem,
    setActiveItem,
    isPanModeActive,
    setIsPanModeActive,
    panAnnouncement,
    setPanAnnouncement,
    hasSelection,
    setHasSelection,
    hasTextSelection,
    setHasTextSelection,
    canZoomToSelection,
    lastPanModeRef,
    hasObjectSelectionForMenus,
    hasLockedSelectionForMenus,
    hasSelfFrozenSelectionForMenus,
    showUnlockSelectedInObjectMenus,
    showUnfreezeSelectedInObjectMenus,
    hasLockedSelectedTextForPanels,
    hasAnyLockedObjects,
    hasAnyFrozenObjects,
    handleSetSelectedShape,
    deactivatePanMode,
    togglePanMode,
    handleActivateTool,
    handleCheckSpelling,
    selectedPhotoId,
    selectedPhotoImageId,
    candidAutoTaggingDataRef,
    isSelectedPhotoPortrait,
    isCaptionEnabled,
  }
}
